import { useForm } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import FormSection, {
  FormSectionProps
} from "@/components/controls/Form/FormSection";
import { getFormDefaultValues } from "@/components/controls/Form/helpers";
import { parseFileFieldValue } from "@/utils/entityFieldsUtils/fileFieldUtils";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { isEmpty } from "lodash";

export const NONE_OPTION_VALUE = "none";

interface Props {
  sections: FormSectionProps[];
  isFormValid: (isValid: boolean, watchAllFields: Record<string, any>) => void;
  onSubmit?: (data: any) => void;
  formRef?: React.RefObject<HTMLFormElement>;
  onSetValue?: (formMethods: { setValue: any; getValues: any }) => void;
  shouldNotResetOnSubmit?: boolean;
  className?: string;
  // Renders and validates only these sections; every field stays registered.
  visibleSectionIds?: string[];
}

const FormNew: React.FC<Props> = ({
  sections,
  isFormValid,
  onSubmit,
  formRef,
  onSetValue,
  shouldNotResetOnSubmit = false,
  className,
  visibleSectionIds
}) => {
  const allFields: FormFieldNewType[] = sections.flatMap(
    (section) => section.fields || []
  );

  const visibleSections = useMemo(
    () =>
      visibleSectionIds
        ? sections.filter((section) => visibleSectionIds.includes(section.id))
        : sections,
    [sections, visibleSectionIds]
  );

  const validatedFields: FormFieldNewType[] = visibleSections.flatMap(
    (section) => section.fields || []
  );

  const [customValidationErrors, setCustomValidationErrors] = useState<
    Record<string, boolean>
  >({});

  // defaultValues is read once at mount and never resynced — callers editing
  // a different record must remount FormNew (see useActivityModals.tsx's
  // per-record modal key) rather than relying on this value updating.
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    unregister,
    watch
  } = useForm({
    mode: "all",
    defaultValues: getFormDefaultValues(allFields)
  });

  const handleFieldValidationChange = useCallback(
    (fieldName: string, hasErrors: boolean) => {
      setCustomValidationErrors((prev) => {
        if (prev[fieldName] === hasErrors) {
          return prev;
        }
        return {
          ...prev,
          [fieldName]: hasErrors
        };
      });
    },
    []
  );

  useEffect(() => {
    if (onSetValue) {
      onSetValue({ setValue, getValues: watch });
    }
  }, [setValue, watch, onSetValue]);

  const watchAllFields = watch();

  const isFieldVisible = useCallback(
    (field: FormFieldNewType) => {
      if (!field.visibleWhenFieldFilled) return true;
      return Boolean(watchAllFields[field.visibleWhenFieldFilled]);
    },
    [watchAllFields]
  );

  // Tracks fields already dropped while hidden. unregister() mutates RHF state
  // and re-renders, and this effect re-runs every render (watch() returns a
  // fresh object each time). Deduping the calls is what keeps that from
  // becoming an infinite render loop — a value check is not enough because
  // watch() keeps surfacing a dropped field's defaultValue.
  const unregisteredHiddenFields = useRef<Set<string>>(new Set());

  useEffect(() => {
    // A field hidden by visibleWhenFieldFilled stays registered in useForm, so
    // its stale value and errors would still flow into submit data and block
    // isFormValid. Drop it from form state until its dependency is filled again.
    allFields.forEach((field) => {
      if (field.visibleWhenFieldFilled && isFieldVisible(field)) {
        // Visible again (and re-registered on render) — allow a future drop.
        unregisteredHiddenFields.current.delete(field.name);
      }
    });
    const hiddenDependentFields = allFields.filter(
      (field) =>
        field.visibleWhenFieldFilled &&
        !isFieldVisible(field) &&
        !unregisteredHiddenFields.current.has(field.name)
    );
    if (hiddenDependentFields.length === 0) return;
    hiddenDependentFields.forEach((field) => {
      unregister(field.name);
      unregisteredHiddenFields.current.add(field.name);
    });
    setCustomValidationErrors((prev) => {
      const fieldsToClear = hiddenDependentFields.filter(
        (field) => field.name in prev
      );
      if (fieldsToClear.length === 0) return prev;
      const next = { ...prev };
      fieldsToClear.forEach((field) => delete next[field.name]);
      return next;
    });
  }, [allFields, isFieldVisible, unregister]);

  useEffect(() => {
    // Check if all required fields are filled (hidden fields are exempt)
    const requiredFieldsFilled = validatedFields
      .filter(isFieldVisible)
      .every((field) => {
        if (!field.required) return true;
        const value = watchAllFields[field.name];
        if (field.type === "multiSelect") {
          return Array.isArray(value) && value.length > 0;
        }
        if (field.type === "multiEmail") {
          return (
            Array.isArray(value) &&
            value.length > 0 &&
            value.some((email) => email.trim() !== "")
          );
        }
        if (field.type === "attachments") {
          // An empty attachments field serializes to "[]", which is a
          // non-empty string — the generic check below reads that as filled and
          // lets a required file field submit with nothing on it.
          return parseFileFieldValue(value).length > 0;
        }
        return value !== undefined && value !== "";
      });

    const hasCustomValidationErrors = Object.values(
      customValidationErrors
    ).some((hasError) => hasError);

    isFormValid(
      requiredFieldsFilled && isEmpty(errors) && !hasCustomValidationErrors,
      watchAllFields
    );
  }, [
    watchAllFields,
    errors,
    validatedFields,
    customValidationErrors,
    isFormValid,
    isFieldVisible
  ]);

  const handleFormSubmit = async (data: any) => {
    try {
      if (onSubmit) {
        await onSubmit(data);
        if (!shouldNotResetOnSubmit) {
          reset();
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const sectionsWithValidation = useMemo(
    () =>
      visibleSections.map((section) => ({
        ...section,
        fields: section.fields?.filter(isFieldVisible).map((field) => ({
          ...field,
          onValidationChange: handleFieldValidationChange,
          hasError: !!errors[field.name] || customValidationErrors[field.name],
          errorMessage: errors[field.name]?.message as string
        }))
      })),
    [
      visibleSections,
      errors,
      customValidationErrors,
      handleFieldValidationChange,
      isFieldVisible
    ]
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(handleFormSubmit)}
      className={className ?? "space-y-5"}
      data-test="form-new"
    >
      {sectionsWithValidation.length > 0 &&
        sectionsWithValidation.map((section, index) => (
          <FormSection
            key={section.id}
            {...section}
            register={register}
            setValue={setValue}
            watch={watch}
            allFields={allFields}
            isLast={index === sectionsWithValidation.length - 1}
          />
        ))}
    </form>
  );
};

export default FormNew;
